import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Plus, Clock, Check, X, Send, AlertTriangle, Package, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AtelierLayout from '@/components/atelier/AtelierLayout';

const STATUS_STYLES = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approuvee', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusee', cls: 'bg-red-100 text-red-700' },
};

const AtelierCommandes = ({ onLogout }) => {
  const [commandes, setCommandes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mechanicName] = useState(() => localStorage.getItem('atelier_mechanic') || 'Atelier');
  const [tab, setTab] = useState('pending');

  const [form, setForm] = useState({
    item_name: '', category: '', quantity: 1, unit: 'piece', urgency: 'normal', description: '',
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [cmdRes, catRes] = await Promise.all([
        axios.get(`${API}/atelier/commandes`),
        axios.get(`${API}/commandes/categories`),
      ]);
      setCommandes(cmdRes.data);
      setCategories(catRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const submit = async () => {
    if (!form.item_name.trim()) { toast.error('Selectionnez ou saisissez un article'); return; }
    try {
      await axios.post(`${API}/atelier/commandes`, {
        ...form,
        requested_by: mechanicName,
      });
      toast.success('Demande envoyee');
      setForm({ item_name: '', category: '', quantity: 1, unit: 'piece', urgency: 'normal', description: '' });
      setSelectedCategory(null);
      setShowForm(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const filtered = tab === 'all' ? commandes : commandes.filter(c => c.status === tab);
  const pendingCount = commandes.filter(c => c.status === 'pending').length;

  return (
    <AtelierLayout onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4" data-testid="atelier-commandes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-blue-600" /> Commandes & Besoins
            </h1>
            {pendingCount > 0 && (
              <p className="text-sm text-amber-600 mt-1">{pendingCount} en attente de validation</p>
            )}
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="new-order-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle demande
          </Button>
        </div>

        {/* New order form */}
        {showForm && (
          <Card className="p-4 border-2 border-blue-200 bg-blue-50/30 space-y-3" data-testid="order-form">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-500" /> Que avez-vous besoin ?
            </h3>

            {/* Category selection */}
            <div>
              <label className="text-xs text-slate-500 font-medium mb-1 block">Categorie</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {categories.map(cat => (
                  <Button
                    key={cat.key}
                    size="sm"
                    variant={selectedCategory?.key === cat.key ? 'default' : 'outline'}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setForm({ ...form, category: cat.key, item_name: '' });
                    }}
                    className="text-xs"
                    data-testid={`cat-${cat.key}`}
                  >
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Item selection from category */}
            {selectedCategory && selectedCategory.items?.length > 0 && (
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1 block">Article</label>
                <Select value={form.item_name} onValueChange={v => setForm({ ...form, item_name: v })}>
                  <SelectTrigger data-testid="item-select">
                    <SelectValue placeholder="Selectionnez un article..." />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCategory.items.map(item => (
                      <SelectItem key={item} value={item}>{item}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">Autre (saisie libre)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Free text if "Autre" category or custom item */}
            {(selectedCategory?.key === 'autre' || form.item_name === '__custom__' || !selectedCategory) && (
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1 block">Nom de l'article</label>
                <Input
                  value={form.item_name === '__custom__' ? '' : form.item_name}
                  onChange={e => setForm({ ...form, item_name: e.target.value })}
                  placeholder="Ex: Joint de culasse, outil special..."
                  data-testid="custom-item-input"
                />
              </div>
            )}

            {/* Quantity + Urgency */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1 block">Quantite</label>
                <Input
                  type="number" min="1"
                  value={form.quantity}
                  onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })}
                  data-testid="qty-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1 block">Unite</label>
                <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="piece">Piece(s)</SelectItem>
                    <SelectItem value="litre">Litre(s)</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="metre">Metre(s)</SelectItem>
                    <SelectItem value="boite">Boite(s)</SelectItem>
                    <SelectItem value="jeu">Jeu(x)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1 block">Urgence</label>
                <Select value={form.urgency} onValueChange={v => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="text-xs text-slate-500 font-medium mb-1 block">Remarque (optionnel)</label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Precision, reference vehicule, etc."
                data-testid="description-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setSelectedCategory(null); }}>Annuler</Button>
              <Button size="sm" onClick={submit} disabled={!form.item_name.trim() || form.item_name === '__custom__'} data-testid="submit-order">
                <Send className="w-3.5 h-3.5 mr-1" /> Envoyer la demande
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          {[
            { key: 'pending', label: 'En attente', count: pendingCount },
            { key: 'approved', label: 'Approuvees' },
            { key: 'rejected', label: 'Refusees' },
            { key: 'all', label: 'Toutes' },
          ].map(t => (
            <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
              {t.label} {t.count > 0 ? `(${t.count})` : ''}
            </Button>
          ))}
        </div>

        {/* Orders list */}
        {loading ? (
          <p className="text-center text-slate-400 py-10">Chargement...</p>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center">
            <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Aucune demande</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(cmd => {
              const st = STATUS_STYLES[cmd.status] || STATUS_STYLES.pending;
              return (
                <Card key={cmd.id} className="p-3" data-testid={`cmd-${cmd.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-slate-900">{cmd.item_name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        {cmd.urgency === 'urgent' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">URGENT</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {cmd.category} — {cmd.quantity} {cmd.unit} — {cmd.requested_by} — {new Date(cmd.requested_at).toLocaleDateString('fr-CH')}
                      </div>
                      {cmd.description && <p className="text-xs text-slate-400 mt-0.5 italic">"{cmd.description}"</p>}
                      {cmd.reviewed_by && (
                        <p className="text-xs mt-0.5 text-slate-400">
                          {cmd.status === 'approved' ? 'Approuvee' : 'Refusee'} par {cmd.reviewed_by}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AtelierLayout>
  );
};

export default AtelierCommandes;
